import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom components
import DefaultCell from "layouts/userdata/components/DefaultCell";
import StatusCell from "layouts/userdata/components/StatusCell";
import CustomerCell from "layouts/userdata/components/CustomerCell";
import SoftSelect from "components/SoftSelect";
import SoftPagination from "components/SoftPagination";
import SoftInput from "components/SoftInput";

function InterviewPending() {
  const [menu, setMenu] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "",
    is_active: "",
    order_by: "id",
    order: "desc",
  });

  const [pagination, setPagination] = useState({
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 25,
  });

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const queryParams = new URLSearchParams();

      if (debouncedSearch) {
        queryParams.append("q", debouncedSearch);
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });

      queryParams.append("page", pagination.current_page);
      queryParams.append("per_page", pagination.per_page);

      const response = await fetch(
        `https://surveys.exchange-ip.com/api/users/pending?${queryParams}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();

      if (data.success) {
        setUsers(data.users.data);
        setPagination((prev) => ({
          ...prev,
          total: data.users.total,
          current_page: data.users.current_page,
          last_page: data.users.last_page,
          per_page: data.users.per_page,
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters, pagination.current_page, pagination.per_page]);

  // Trigger API call when filters, pagination, or debounced search changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    console.log(filters?.status);
    // Reset to first page when filters change
    setPagination((prev) => ({
      ...prev,
      current_page: 1,
    }));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  const handlePerPageChange = (perPage) => {
    setPagination((prev) => ({
      ...prev,
      per_page: perPage,
      current_page: 1,
    }));
  };

  if (loading)
    <div>
      <CircularProgress color="info" />
    </div>;

  // const NameCell = ({ value, row }) => (
  //   <CustomerCell
  //     name={value}
  //     image={row.original.image || ""}
  //     color={row.original.color || "dark"}
  //   />
  // );

  // NameCell.propTypes = {
  //   value: PropTypes.string.isRequired,
  //   row: PropTypes.shape({
  //     original: PropTypes.shape({
  //       image: PropTypes.string,
  //       color: PropTypes.string,
  //     }),
  //   }).isRequired,
  // };

  // const EmailCell = ({ value }) => <DefaultCell value={value} />;
  // EmailCell.propTypes = { value: PropTypes.string };

  const BranchCell = ({ value }) => <DefaultCell value={value} />;
  BranchCell.propTypes = { value: PropTypes.string };

  const DepartmentCell = ({ value }) => <DefaultCell value={value} />;
  DepartmentCell.propTypes = { value: PropTypes.string };

  const DesignationCell = ({ value }) => <DefaultCell value={value} />;
  DesignationCell.propTypes = { value: PropTypes.string };

  const RoleCell = ({ value }) => <DefaultCell value={value} />;
  RoleCell.propTypes = { value: PropTypes.string };

  const ShiftCell = ({ value }) => <DefaultCell value={value} />;
  ShiftCell.propTypes = { value: PropTypes.string };

  const ShiftCodeCell = ({ value }) => <DefaultCell value={value} />;
  ShiftCodeCell.propTypes = { value: PropTypes.string };

  const ShiftTimeCell = ({ value }) => <DefaultCell value={value} />;
  ShiftTimeCell.propTypes = { value: PropTypes.string };

  const StatusTableCell = ({ value }) => {
    let icon, color, status;

    switch (value) {
      case "selected":
        icon = "done";
        color = "success";
        status = "Selected";
        break;
      case "shortlisted":
        icon = "star";
        color = "info";
        status = "Shortlisted";
        break;
      case "rejected":
        icon = "close";
        color = "error";
        status = "Rejected";
        break;
      case "pending":
      default:
        icon = "hourglass_empty";
        color = "warning";
        status = "Pending";
        break;
    }

    return <StatusCell icon={icon} color={color} status={status} />;
  };

  StatusTableCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  // const dataTableData = {
  //   columns: [
  //     { Header: "Name", accessor: "name", Cell: NameCell },
  //     { Header: "Email", accessor: "email", Cell: EmailCell },
  //     { Header: "Branch", accessor: "branch.name", Cell: BranchCell },
  //     { Header: "Department", accessor: "department.name", Cell: DepartmentCell },
  //     { Header: "Designation", accessor: "designation.name", Cell: DesignationCell },
  //     { Header: "Role", accessor: "role", Cell: RoleCell },
  //     { Header: "Shift", accessor: "shift.type", Cell: ShiftCell },
  //     { Header: "Shift Code", accessor: "shift.shift_code", Cell: ShiftCodeCell },
  //     { Header: "Shift Time", accessor: "shift_time", Cell: ShiftTimeCell },
  //     { Header: "Status", accessor: "status", Cell: StatusTableCell },
  //   ],
  //   rows: users,
  // };

  // Cell Components
  const IdCell = ({ value }) => <DefaultCell value={value} />;
  const NameCell = ({ value }) => <DefaultCell value={value} />;
  const LastNameCell = ({ value }) => <DefaultCell value={value} />;
  const DobCell = ({ value }) => <DefaultCell value={value} />;
  const GenderCell = ({ value }) => <DefaultCell value={value} />;
  const PhoneCell = ({ value }) => <DefaultCell value={value} />;
  const EmailCell = ({ value }) => <DefaultCell value={value} />;
  const QualificationCell = ({ value }) => <DefaultCell value={value} />;

  // PropTypes
  const cellPropTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  };

  IdCell.propTypes = cellPropTypes;
  NameCell.propTypes = cellPropTypes;
  LastNameCell.propTypes = cellPropTypes;
  DobCell.propTypes = cellPropTypes;
  GenderCell.propTypes = cellPropTypes;
  PhoneCell.propTypes = cellPropTypes;
  EmailCell.propTypes = cellPropTypes;
  QualificationCell.propTypes = cellPropTypes;

  const dataTableData = {
    columns: [
      { Header: "ID", accessor: "id", Cell: IdCell },
      { Header: "First Name", accessor: "name", Cell: NameCell },
      { Header: "Last Name", accessor: "last_name", Cell: LastNameCell },
      { Header: "DOB", accessor: "dob", Cell: DobCell },
      { Header: "Gender", accessor: "gender", Cell: GenderCell },
      { Header: "Phone Number", accessor: "phone_number", Cell: PhoneCell },
      { Header: "Email", accessor: "email", Cell: EmailCell },
      { Header: "Qualification", accessor: "qualification", Cell: QualificationCell },
    ],
    rows: users,
  };

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        onClick={() => {
          handleFilterChange("status", "active");
          closeMenu();
        }}
      >
        Status: Active
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleFilterChange("status", "inactive");
          closeMenu();
        }}
      >
        Status: Inactive
      </MenuItem>
      <Divider sx={{ margin: "0.5rem 0" }} />
      <MenuItem
        onClick={() => {
          handleFilterChange("status", "");
          closeMenu();
        }}
      >
        <SoftTypography variant="button" color="error" fontWeight="regular">
          Remove Filter
        </SoftTypography>
      </MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mb={3}>

        <Card>
          <SoftBox display="flex" flexDirection="column" gap={1} p={2}>
            <SoftBox display="flex" justifyContent="flex-end">
              <input
                className="form-control"
                style={{ width: "300px" }}
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </SoftBox>

            <SoftBox display="flex" alignItems="center" justifyContent="space-between">
              <SoftTypography variant="caption" color="secondary">
                Showing {users.length} of {pagination.total} entries
              </SoftTypography>
              <SoftPagination variant="gradient" color="info">
                {pagination.current_page > 1 && (
                  <SoftPagination
                    item
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                  </SoftPagination>
                )}

                {Array.from({ length: pagination.last_page }, (_, i) => (
                  <SoftPagination
                    key={i}
                    item
                    active={pagination.current_page === i + 1}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </SoftPagination>
                ))}

                {pagination.current_page < pagination.last_page && (
                  <SoftPagination
                    item
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                  </SoftPagination>
                )}
              </SoftPagination>
            </SoftBox>

            {loading ? (
              <SoftBox display="flex" justifyContent="center" alignItems="center" height="50vh">
                <CircularProgress color="info" size={40} thickness={4} />
              </SoftBox>
            ) : (
              <DataTable
                table={dataTableData}
                entriesPerPage={{
                  defaultValue: pagination.per_page,
                  entries: [10, 25, 50, 100],
                }}
                canSearch={false}
                isLoading={loading}
                pagination={{
                  page: pagination.current_page - 1,
                  totalRows: pagination.total,
                  rowsPerPage: pagination.per_page,
                  onChangePage: (page) => handlePageChange(page + 1),
                }}
              />
            )}

          </SoftBox>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InterviewPending;
